import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, LockIcon, PencilIcon } from 'lucide-react';
import { useFormState, FormData } from '../hooks/useFormState';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { TCPAConsent } from '../components/TCPAConsent';
import { usePageMeta } from '../hooks/usePageMeta';
import { educationLevels, degreeInterestOptions, focusOptions, specializationOptions } from '../data/degrees';
interface LocationState {
  degreeInterest: string;
  areaOfStudy: string;
  specialization: string;
}
export function ExpandedFormPage() {
  usePageMeta('Complete Your Profile | OnlineCollegeGuide', 'Fill out your profile to get matched with accredited online degree programs that fit your goals, schedule, and budget.', {
    ogImage: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | null;
  // This page collects PII, so LeadiD and TrustedForm must be running here too.
  useLeadTracking();
  // Redirect if no state
  useEffect(() => {
    if (!state) {
      navigate('/', {
        replace: true
      });
    }
    window.scrollTo(0, 0);
  }, [state, navigate]);
  const {
    formData,
    errors,
    updateFormData,
    validateExpandedForm
  } = useFormState(state ? {
    degreeInterest: state.degreeInterest,
    areaOfStudy: state.areaOfStudy,
    specialization: state.specialization
  } : undefined);
  if (!state) return null;
  // Resolve labels for the summary bar
  const degreeName = degreeInterestOptions.find(d => d.value === state.degreeInterest)?.type || '';
  const focusName = focusOptions.find(f => f.value === state.areaOfStudy)?.type || '';
  const specName = specializationOptions.find(s => s.value === state.specialization)?.type || '';
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateExpandedForm()) {
      // TODO: Send formData to matching API here
      const cleanedFormData = {
        ...formData,
        phone: stripPhone(formData.phone)
      };
      console.log('Form submitted:', cleanedFormData);
      navigate('/results', {
        state: {
          formData: cleanedFormData
        }
      });
    }
  };
  const selectClasses = (field: keyof FormData) => `block w-full px-3 py-3 text-sm border rounded-lg bg-white appearance-none cursor-pointer ${errors[field] ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const inputClasses = (field: keyof FormData) => `block w-full px-3 py-3 text-sm border rounded-lg shadow-sm ${errors[field] ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const radioGroupClasses = (field: keyof FormData, value: string) => `flex-1 cursor-pointer border rounded-lg py-3 px-4 text-center text-sm font-medium transition-all duration-150 ${formData[field] === value ? 'border-navy bg-navy/5 text-navy ring-1 ring-navy' : 'border-gray-300 text-gray-600 hover:border-navy/40'}`;
  return <main className="flex-grow bg-light min-h-screen" data-id="element-1570" data-mp="339ykcbzpvefv">
      {/* Top bar with selections summary */}
      <div className="hero-bg text-white" data-id="element-1571" data-mp="2ocfnemcvxuxc">
        <div className="max-w-2xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-1572" data-mp="2td2c87dqcubu">
          <div className="flex items-center gap-2 text-sm" data-id="element-1573" data-mp="2fdu7z02pmw0s">
            <ShieldCheckIcon className="w-4 h-4 text-gold flex-shrink-0" data-id="element-1574" data-mp="2ffdrczjnxn6s" />
            <span className="text-gray-300" data-id="element-1575" data-mp="2kcxfcr65xebx">Your selections:</span>
            <span className="font-semibold" data-id="element-1576" data-mp="dleck47c0npc">{degreeName}</span>
            <span className="text-gray-400" data-id="element-1577" data-mp="dk3d2gkx6241">›</span>
            <span className="font-semibold" data-id="element-1578" data-mp="ijzi8j5crzl6">{focusName}</span>
            <span className="text-gray-400" data-id="element-1579" data-mp="2eo5ma7p4naf1">›</span>
            <span className="font-semibold text-gold" data-id="element-1580" data-mp="2enndkbla33q8">{specName}</span>
          </div>
          <Link to="/" className="flex items-center gap-1 text-xs text-gray-300 hover:text-white transition-colors" data-id="element-1581" data-mp="29q3xr78xmiw6">
            <PencilIcon className="w-3 h-3" data-id="element-1582" data-mp="24oenvuxg4b9o" />
            Edit
          </Link>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="max-w-2xl mx-auto px-4 py-8 sm:py-12" data-id="element-1583" data-mp="20o4jgmpaxswx">
        {/* Progress */}
        <div className="mb-8" data-id="element-1584" data-mp="1lyyml5dxses7">
          <div className="flex justify-between items-center mb-2" data-id="element-1585" data-mp="1m09mgu49h2e1">
            <span className="text-xs font-semibold text-navy uppercase tracking-wider" data-id="element-1586" data-mp="1h0x0ebl7rzci">
              Step 2 of 2
            </span>
            <span className="text-xs font-medium text-gray-500" data-id="element-1587" data-mp="1cya7s8gpyae9">
              Almost done!
            </span>
          </div>
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-1588" data-mp="181tgx6i4xhxh">
            <div className="h-full bg-gold rounded-full w-1/2 transition-all duration-500" data-id="element-1589" data-mp="3jmsnve6gi9eg" />
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden" data-id="element-1590" data-mp="3sn8pei3n36go">
          <div className="bg-navy px-6 py-4" data-id="element-1591" data-mp="1h79nw8qboop">
            <h1 className="text-white font-bold text-lg tracking-wide text-center" data-id="element-1592" data-mp="1gxjo8nophj0">
              Complete Your Profile
            </h1>
            <p className="text-gray-300 text-xs text-center mt-1" data-id="element-1593" data-mp="34y5yejn4819i">
              Tell us a bit about yourself so we can find the best matches.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6" data-id="element-1594" data-mp="39vxxpyq3apnq">
            {/* Certification fields. The scripts write into these by id/name,
                so they stay uncontrolled and must live inside the form. */}
            <input type="hidden" id="leadid_token" name="leadid_token" data-id="element-1595" data-mp="2m7etbua5ux0x" />
            <input type="hidden" id="xxTrustedFormCertUrl" name="xxTrustedFormCertUrl" data-id="element-1596" data-mp="2m5lie2silqn4" />
            {/* Education Level */}
            <div data-id="element-1597" data-mp="3kizijawxfvv5">
              <label htmlFor="currentEducation" className="block text-sm font-semibold text-gray-800 mb-1.5" data-id="element-1598" data-mp="3khenlk6paawr">
                Highest Level of Education Completed
              </label>
              <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses('currentEducation')} data-id="element-1599" data-mp="3ua3xwbqiliqi">
                <option value="" data-id="element-1600" data-mp="31m7tslym8ojp">-- Please Select --</option>
                {educationLevels.map(level => <option key={level} value={level} data-id="element-1601" data-mp="3fl7g3mynorcp">
                    {level}
                  </option>)}
              </select>
              {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-1602" data-mp="utx8npv77wos">
                  {errors.currentEducation}
                </p>}
            </div>

            {/* US Citizen */}
            <div data-id="element-1603" data-mp="px6sl2je913d">
              <label className="block text-sm font-semibold text-gray-800 mb-2" data-id="element-1604" data-mp="yc4h8xu53b9r">
                Are you a U.S. Citizen?
              </label>
              <div className="flex gap-3" data-id="element-1605" data-mp="12ehlfht8n14f">
                <div onClick={() => updateFormData('isUSCitizen', 'yes')} className={radioGroupClasses('isUSCitizen', 'yes')} role="radio" aria-checked={formData.isUSCitizen === 'yes'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isUSCitizen', 'yes')} data-id="element-1606" data-mp="12bdbe3t3db5u">
                  Yes
                </div>
                <div onClick={() => updateFormData('isUSCitizen', 'no')} className={radioGroupClasses('isUSCitizen', 'no')} role="radio" aria-checked={formData.isUSCitizen === 'no'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isUSCitizen', 'no')} data-id="element-1607" data-mp="1m1fjykv1vfqj">
                  No
                </div>
              </div>
              {errors.isUSCitizen && <p className="mt-1 text-xs text-red-600" data-id="element-1608" data-mp="2005hu2pp1jfy">
                  {errors.isUSCitizen}
                </p>}
            </div>

            {/* Military */}
            <div data-id="element-1609" data-mp="4q75tcdxusmx">
              <label className="block text-sm font-semibold text-gray-800 mb-2" data-id="element-1610" data-mp="4qpb0yikwh6z">
                U.S. Military Affiliation?
              </label>
              <div className="flex gap-3" data-id="element-1611" data-mp="3r5i61s6pkr6h">
                <div onClick={() => updateFormData('isMilitary', 'yes')} className={radioGroupClasses('isMilitary', 'yes')} role="radio" aria-checked={formData.isMilitary === 'yes'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isMilitary', 'yes')} data-id="element-1612" data-mp="3r6jsest6d03i">
                  Yes
                </div>
                <div onClick={() => updateFormData('isMilitary', 'no')} className={radioGroupClasses('isMilitary', 'no')} role="radio" aria-checked={formData.isMilitary === 'no'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isMilitary', 'no')} data-id="element-1613" data-mp="iq888w9uq84i">
                  No
                </div>
              </div>
              {errors.isMilitary && <p className="mt-1 text-xs text-red-600" data-id="element-1614" data-mp="2ufiy9zydtwoy">{errors.isMilitary}</p>}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 pt-6" data-id="element-1615" data-mp="19y02iuwbyy2g">
              <h3 className="text-sm font-semibold text-gray-800 mb-4" data-id="element-1616" data-mp="1ewb98wrbwsgg">
                Contact Information
              </h3>
            </div>

            {/* Name Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1617" data-mp="1iwlrp8wb5bc7">
              <div data-id="element-1618" data-mp="1npn6zi0ffcwb">
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1619" data-mp="1nq6j2i3eb72f">
                  First Name
                </label>
                <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses('firstName')} placeholder="Jane" data-id="element-1620" data-mp="1xm9ru6sr63yc" />
                {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-1621" data-mp="26mg27jt5mnta">
                    {errors.firstName}
                  </p>}
              </div>
              <div data-id="element-1622" data-mp="89xwmgi3iscb">
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1623" data-mp="c9gh5b6091em">
                  Last Name
                </label>
                <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses('lastName')} placeholder="Doe" data-id="element-1624" data-mp="1fis8lbo4b2e3" />
                {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-1625" data-mp="1ogki1z75rftk">{errors.lastName}</p>}
              </div>
            </div>

            {/* Email */}
            <div data-id="element-1626" data-mp="1ygl1rm8xvxya">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1627" data-mp="1yh4kv6abssdw">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses('email')} placeholder="jane.doe@example.com" data-id="element-1628" data-mp="22g3jya9dluxf" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1629" data-mp="2c9kh9o8q1s5u">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div data-id="element-1630" data-mp="2m5ntxo179971">
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1631" data-mp="2m54au3ztcerf">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses('phone')} placeholder="(555)123-4567" maxLength={13} data-id="element-1632" data-mp="ajvb60z2b212" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-1633" data-mp="3rqxi9497io61">{errors.phone}</p>}
            </div>

            {/* Address + Zip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1634" data-mp="t9bh53tinmov">
              <div className="sm:col-span-2" data-id="element-1635" data-mp="o9yscdj345jn">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1636" data-mp="ob9vqc8mbba8">
                  Street Address{' '}
                  <span className="text-gray-400 font-normal" data-id="element-1637" data-mp="jgvzk7m4t04i">(optional)</span>
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => updateFormData('address', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-navy focus:border-navy" placeholder="123 Main St" data-id="element-1638" data-mp="eib6cs5argpk" />
              </div>
              <div data-id="element-1639" data-mp="31j16x1dtmg7m">
                <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1640" data-mp="36fi22fjftuu7">
                  Zip Code
                </label>
                <input type="text" id="zipCode" value={formData.zipCode} onChange={e => updateFormData('zipCode', e.target.value)} className={inputClasses('zipCode')} placeholder="12345" maxLength={5} data-id="element-1641" data-mp="2rqlmzyiyrhk3" />
                {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-1642" data-mp="2isjrtzlwvj4q">{errors.zipCode}</p>}
              </div>
            </div>

            {/* TCPA */}
            <TCPAConsent checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} data-id="element-1643" data-mp="28ssxbxxcp44n" />

            {/* Submit */}
            <button type="submit" className="w-full bg-cta hover:bg-cta-dark text-white font-bold py-4 px-6 rounded-lg text-base tracking-wide uppercase transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cta-dark shadow-md" data-id="element-1644" data-mp="70mrioj3v0yd">
              Get My Results
            </button>

            {/* Security note */}
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400 pt-2" data-id="element-1645" data-mp="1rk8fmk9s84sr">
              <LockIcon className="w-3.5 h-3.5" data-id="element-1646" data-mp="2694ky9kgf3po" />
              Your information is secure and will never be sold.
            </div>
          </form>
        </div>
      </motion.div>
    </main>;
}